// src/receptionist/screens/BusinessProfile.tsx
//
// Who the business is, and the WhatsApp number the receptionist answers on.
// The number is the part with real consequences: moving an existing business
// number onto the WhatsApp Business API removes it from the WhatsApp Business
// app, which is a one-way door for the merchant's own phone. That is stated
// on this screen rather than in a footnote, because merchants have been
// surprised by it.

import { useEffect, useState } from "react";
import { Building2Icon, CalendarClockIcon, PlusIcon, XIcon } from "lucide-react";
import { PageHeader, Card, CardHeader, CardBody, Button } from "../components/kit";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Textarea } from "@component/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@component/select";
import { PickerInput, SectionLabel, RowAction } from "../components/ui";
import { cn } from "@oura/lib/cn";
import { DAY_LABELS, type MerchantCategory, type MerchantConfig } from "../merchant";
import type { MerchantScreenProps } from "../merchantState";
import { WhatsappOnboarding } from "../components/WhatsappOnboarding";

const CATEGORIES: { value: MerchantCategory; label: string }[] = [
    { value: "salon", label: "Hair salon" },
    { value: "spa", label: "Spa or wellness" },
    { value: "clinic", label: "Skin or aesthetic clinic" },
    { value: "dental", label: "Dental clinic" },
];

const ALL_LANGUAGES = ["English", "Bahasa Melayu", "中文", "தமிழ்"];

/** `parts="business"`: the details and the hours only, for the Setup page's first step (#4796). */
export function BusinessProfileScreen({ merchant: server, onSave, parts }: MerchantScreenProps & { parts?: "business" }) {
    // A local draft of the server's copy; replaced wholesale whenever the prop
    // changes so the form never shows an optimistic copy the server rejected.
    const [m, setM] = useState<MerchantConfig>(server);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dateDraft, setDateDraft] = useState<{ date: string; reason: string } | null>(null);
    useEffect(() => { setM(server); setDirty(false); setDateDraft(null); }, [server]);

    /**
     * The two rules the server enforces with a 422, held here so the reader
     * sees why Save is off instead of a toast after the fact
     * (ourteam-backend#4667): at least one language, and an opening-hours row
     * is both set or both null — a half-set row is rejected.
     */
    const noLanguage = m.languages.length === 0;
    const halfSetDay = m.hours.find((h) => (h.open === null || h.open === "") !== (h.close === null || h.close === ""));
    const blocked = noLanguage
        ? "Pick at least one language the AI replies in."
        : halfSetDay
            ? `${DAY_LABELS[halfSetDay.day]} needs both an opening and a closing time.`
            : null;

    const save = async () => {
        if (blocked) return;
        setSaving(true);
        try { if (await onSave(m)) setDirty(false); } finally { setSaving(false); }
    };
    const patch = (next: Partial<MerchantConfig>) => { setM((p) => ({ ...p, ...next })); setDirty(true); };

    const toggleLanguage = (lang: string) =>
        patch({
            languages: m.languages.includes(lang)
                ? m.languages.filter((l) => l !== lang)
                : [...m.languages, lang],
        });

    return (
        <div>
            <PageHeader
                title="Business profile"
                subtitle="Who you are, when you open, and the number the receptionist answers on."
                action={
                    <span className="flex items-center gap-3">
                        {dirty && blocked && <span className="text-xs text-amber-700">{blocked}</span>}
                        <Button disabled={!dirty || saving || !!blocked} onClick={() => void save()}>{saving ? "Saving…" : "Save changes"}</Button>
                    </span>
                }
            />

            <div className="grid items-start gap-5 md:grid-cols-2">
                <Card>
                    <CardHeader title="Business details" icon={Building2Icon} description="Used in every reply the AI sends." />
                    <CardBody className="space-y-3 pt-0">
                        <div className="space-y-2">
                            <SectionLabel title="Business name" required htmlFor="rcp-name" />
                            <Input id="rcp-name" value={m.name} onChange={(e) => patch({ name: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <SectionLabel
                                title="Category"
                                required
                                subtitle="A clinic keeps medical questions locked to a human, whatever else is configured."
                            />
                            <Select value={m.category} onValueChange={(v) => patch({ category: v as MerchantCategory })}>
                                <SelectTrigger className="w-full border border-gray-300 dark:border-gray-700"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <SectionLabel title="Outlet" htmlFor="rcp-outlet" />
                            <Input id="rcp-outlet" value={m.outlet} onChange={(e) => patch({ outlet: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <SectionLabel title="Address" subtitle="The AI reads this out when someone asks where you are." htmlFor="rcp-address" />
                            {/* Taller than a default field: a Malaysian address
                                runs to three or four lines and was scrolling
                                inside a box barely taller than one. */}
                            <Textarea
                                id="rcp-address"
                                value={m.address}
                                onChange={(e) => patch({ address: e.target.value })}
                                className="min-h-[100px] resize-y"
                            />
                        </div>
                        <div className="space-y-2">
                            <SectionLabel
                                title="Number customers know you by"
                                subtitle="Shown on your profile. The number the receptionist answers on is on the WhatsApp card."
                                htmlFor="rcp-number"
                            />
                            <Input id="rcp-number" value={m.whatsappNumber} onChange={(e) => patch({ whatsappNumber: e.target.value })} />
                        </div>
                        <div>
                            <div className="mb-1.5"><SectionLabel title="Languages the AI replies in" required /></div>
                            <div className="flex flex-wrap gap-1.5">
                                {ALL_LANGUAGES.map((l) => {
                                    const on = m.languages.includes(l);
                                    return (
                                        <button
                                            key={l}
                                            type="button"
                                            onClick={() => toggleLanguage(l)}
                                            className={cn(
                                                "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                                                on ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                                            )}
                                        >
                                            {l}
                                        </button>
                                    );
                                })}
                            </div>
                            <span className="mt-1.5 block text-xs text-gray-500">
                                The AI picks up the language from the customer's first message and stays in it,
                                even when they mix languages mid-sentence. The first one listed is what it falls
                                back to when a customer writes in a language you have not enabled.
                            </span>
                        </div>
                    </CardBody>
                </Card>

                <div className="space-y-5">
                {/* The WhatsApp half (#3613): connection, checklist, template pack —
                    all from GET .../whatsapp, never from the config's projections. */}
                {parts !== "business" && <WhatsappOnboarding merchantId={server.id} typedNumber={m.whatsappNumber} />}

                <Card>
                    <CardHeader title="Opening hours" icon={CalendarClockIcon} description="Outside these, the AI switches to after-hours behaviour." />
                    <CardBody className="pt-0">
                        {/* Once the column is wide enough, the closed dates sit
                            beside the week rather than under it — seven short
                            rows left the rest of the card empty. */}
                        {/* One column, and it has to stay one.
                            This card lives in the right-hand half of the page,
                            which is 572px at a 1440px viewport. Splitting that
                            in two gave each side 270px for a row that measures
                            407px — the action wrapped onto a second line and the
                            week overflowed into the closed dates beside it. No
                            viewport breakpoint can see that: the constraint is
                            the column's width, not the window's, and even a
                            1920px screen leaves the halves at ~390px. */}
                        <div className="space-y-1.5">
                            {m.hours.map((h) => (
                                <div key={h.day} className="grid grid-cols-[2.5rem_1fr_auto] items-center gap-3 border-t border-gray-100 py-2 first:border-t-0">
                                    <span className="text-sm font-medium text-gray-700">{DAY_LABELS[h.day]}</span>
                                    {h.open === null ? (
                                        <span className="text-sm text-gray-400">Closed</span>
                                    ) : (
                                        <span className="flex min-w-0 items-center gap-2">
                                            <PickerInput
                                                kind="time" value={h.open ?? ""} aria-label={`${DAY_LABELS[h.day]} opens`}
                                                onChange={(e) => patch({ hours: m.hours.map((x) => x.day === h.day ? { ...x, open: e.target.value } : x) })}
                                                className="flex-1"
                                            />
                                            <span className="flex-shrink-0 text-xs text-gray-400">to</span>
                                            <PickerInput
                                                kind="time" value={h.close ?? ""} aria-label={`${DAY_LABELS[h.day]} closes`}
                                                onChange={(e) => patch({ hours: m.hours.map((x) => x.day === h.day ? { ...x, close: e.target.value } : x) })}
                                                className="flex-1"
                                            />
                                        </span>
                                    )}
                                    {/* Its own track, so it never wraps and never
                                        drifts: the seven actions line up. */}
                                    <RowAction
                                        className="whitespace-nowrap"
                                        onClick={() => patch({
                                            hours: m.hours.map((x) => x.day === h.day
                                                ? (x.open === null ? { ...x, open: "10:00", close: "19:00" } : { ...x, open: null, close: null })
                                                : x),
                                        })}
                                    >
                                        {h.open === null ? "Open this day" : "Mark closed"}
                                    </RowAction>
                                </div>
                            ))}
                        </div>

                        <div className="mt-5 border-t border-gray-100 pt-4">
                            <p className="mb-1 text-sm font-medium text-gray-900">Closed dates</p>
                            <p className="mb-2.5 text-xs text-gray-500">One-off days the AI should treat as shut, whatever the week says.</p>
                            <div className="flex flex-wrap gap-1.5">
                                {m.closedDates.map((d) => (
                                    <span key={d.date} className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
                                        {d.date} · {d.reason}
                                        <button
                                            type="button"
                                            onClick={() => patch({ closedDates: m.closedDates.filter((x) => x.date !== d.date) })}
                                            aria-label={`Remove ${d.reason}`}
                                        >
                                            <XIcon className="h-3 w-3 text-gray-400 hover:text-gray-700" />
                                        </button>
                                    </span>
                                ))}
                                {dateDraft === null && (
                                    <button
                                        type="button"
                                        onClick={() => setDateDraft({ date: "", reason: "" })}
                                        className="inline-flex h-7 items-center gap-1 rounded-full bg-white px-3 text-xs font-medium text-gray-900 ring-1 ring-inset ring-black/[0.08] hover:bg-gray-50"
                                    >
                                        <PlusIcon className="h-3 w-3" /> Add
                                    </button>
                                )}
                            </div>

                            {dateDraft !== null && (
                                <div className="mt-3 space-y-2 rounded-xl bg-gray-50 p-3">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="rcp-closed-date" className="text-sm font-medium">Date</Label>
                                        <PickerInput
                                            id="rcp-closed-date"
                                            kind="date"
                                            autoFocus
                                            value={dateDraft.date}
                                            onChange={(e) => setDateDraft({ ...dateDraft, date: e.target.value })}
                                            className="bg-white"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="rcp-closed-reason" className="text-sm font-medium">Reason</Label>
                                        <Input
                                            id="rcp-closed-reason"
                                            value={dateDraft.reason}
                                            placeholder="e.g. Malaysia Day"
                                            onChange={(e) => setDateDraft({ ...dateDraft, reason: e.target.value })}
                                            className="bg-white"
                                        />
                                    </div>
                                    {m.closedDates.some((d) => d.date === dateDraft.date) && (
                                        <p className="text-xs text-red-600">That date is already on the list.</p>
                                    )}
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            disabled={
                                                !dateDraft.date
                                                || !dateDraft.reason.trim()
                                                || m.closedDates.some((d) => d.date === dateDraft.date)
                                            }
                                            onClick={() => {
                                                patch({
                                                    closedDates: [...m.closedDates, { date: dateDraft.date, reason: dateDraft.reason.trim() }]
                                                        .sort((a, b) => a.date.localeCompare(b.date)),
                                                });
                                                setDateDraft(null);
                                            }}
                                        >
                                            Add date
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => setDateDraft(null)}>Cancel</Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </CardBody>
                </Card>
                </div>
            </div>
        </div>
    );
}
